import {
  Box,
  LinearProgress,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { API_ENDPOINT } from '../../../configurations/global.config';
import React, { useContext, useEffect, useState } from 'react';
import StorefrontIcon from '@mui/icons-material/Storefront';
import axios, { AxiosResponse } from 'axios';
import { format } from 'date-fns';
import { IPickupTask } from '../../../interfaces/delivery-company/tasks';
import { AuthContext } from '../../../contexts/user-context/user-context';
import {
  NotificationTemplate,
  OrderType,
  OrganisationSettings,
} from '../../../interfaces/delivery-company/orders';
import { PickupTaskState } from '../../../enums/tasks';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import MultiPackageScanner, {
  timestampedMessage,
  validationResult,
} from '../../common/multi-package-scanner/multi-package-scanner';
import { DeliveryState, Workflow } from '../../../enums/orders';
import { GenericTableProps } from '../../common/generic-table/utils';
import {
  DecodingWithOrderVal,
  generateUID,
  transformOrdersToDecodingWithOrderVal,
  transformPendingBarcodesToDecodingWithOrderVal,
} from '../../../utils/encoding';
import SnackBar from '../../common/snack-bar/snack-bar';
import SendMessageDialog from '../common/send-message-dialog/send-message-dialog';
import PaidIcon from '@mui/icons-material/Paid';
import AddressWithIcon from '../../common/styles/address-with-icon';
import MarkAsFailedDialog from '../common/mark-as-failed-dialog';
import { pickup_failure_reasons } from './constants';
import AddNotesDialog from '../common/add-notes-dialog';
import { showPickupTaskState } from '../../common/styles/pickup-task.styles';
import { IconWithText } from '../../common/styles/components';
import { get_notes } from '../../../utils/notes';
import { add, displayMoney } from '../../../utils/money';
import { get_cod } from '../../../utils/cod';
import StaticGoogleMap from '../../common/static-google-map/static-google-map';
import { extractMapsPlaceLink } from '../../../utils/locations';
import OrderNotes from '../../common/order-notes/order-notes';
import { red } from '@mui/material/colors';
import { display_phone_number } from '../../../utils/phone_numbers';
import { MonetizationOn } from '@mui/icons-material';
import { compute_total_pop } from '../../common/report-builder/utils';
import { ClearIcon } from '@mui/x-date-pickers';
import { pickupTaskToQRcodeID } from '../../../utils/pickup-tasks';
import ItemsCount from '../../common/labels-input/items-count/items-count';
import QRCodeBox from '../../common/qrcode-box/qrcode-box';
import { buildResolveTemplateArgsForPickup } from '../../delivery-company-components/settings/notification-manager/utils';

interface DriverPickupTaskPageOnlyOrderProps {
  pickuptaskId: string;
}

function DriverPickupTaskPageOnlyOrder({
  pickuptaskId,
}: DriverPickupTaskPageOnlyOrderProps) {
  const { user, config } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');
  const [pickupData, setPickupData] = useState<IPickupTask>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [markAsFailedDialogOpen, setMarkAsFailedDialogOpen] =
    React.useState(false);
  const [addNoteDialogOpen, setAddNoteDialogOpen] = React.useState(false);
  const [snackBar, setSnackBar] = React.useState<timestampedMessage>();
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = React.useState(false);
  const [notificationTemplates, setNotificationTemplates] =
    React.useState<NotificationTemplate[]>();

  const handleSendWhatAppMessage = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/pickup-tasks/notify/`;
    const requestData = {
      message: template,
      task_ids: [pickupData?.id],
    };

    return axios.post(endpoint, requestData, config);
  };

  const handleResetAllScannedOrders = async (
    allPackages: DecodingWithOrderVal[]
  ) => {
    setIsLoading(true);
    const pckgs = allPackages.filter((item) => item.type === 'permanent');
    const idsToReset: string[] = [];
    for (const pkg of pckgs) {
      if (
        pckgs.filter((x) => x.order.id == pkg.order.id).length ==
        pkg.number_of_packages
      ) {
        if (!idsToReset.includes(pkg.order.id)) {
          idsToReset.push(pkg.order.id);
        }
      }
    }
    const endpoint = `${API_ENDPOINT}/api/v1/order/`;
    const requestData = {
      delivery_state: DeliveryState.PICKUP_ASSIGNED,
    };
    let response: AxiosResponse<any, any>;
    for (const orderId of idsToReset) {
      response = await axios.patch(
        endpoint + orderId + '/',
        requestData,
        config
      );
    }
    await axios.patch(
      `${API_ENDPOINT}/api/v1/pickup-task/${pickupData!.id}/`,
      { pending_barcodes: [] },
      config
    );
    setPickupData({ ...pickupData!, pending_barcodes: [] });
    setIsLoading(false);
    return true;
  };

  const handleAddNoteMenuClick = () => {
    setAddNoteDialogOpen(true);
    handleMenuClose();
  };

  const changeTaskStatus = (newStatus: PickupTaskState) => {
    setPickupData((prev) => (prev ? { ...prev, status: newStatus } : prev));

    if (newStatus == PickupTaskState.COMPLETED) {
      const newValue = {
        timestamp: Date.now(),
        content: 'Task Picked Up Successfully',
        level: 'success',
      };
      setSnackBar(newValue as timestampedMessage);
    }
  };

  const handleStartTask = async (id: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/${id}/`;
    const requestData = {
      status: PickupTaskState.IN_PROGRESS,
    };
    try {
      await axios.patch(endpoint, requestData, config);
      return true;
    } catch (error) {
      return false;
    }
  };
  const handleStartOrResumeTaskMenuClick = async () => {
    const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/`;
    const requestData = {
      status: PickupTaskState.IN_PROGRESS,
    };
    try {
      const response = await axios.patch(
        endpoint + pickupData?.id + '/',
        requestData,
        config
      );

      if (response?.status === 200) {
        changeTaskStatus(PickupTaskState.IN_PROGRESS);
        const newValue = {
          timestamp: Date.now(),
          content: 'Task Started Successfully',
          level: 'success',
        };
        setSnackBar(newValue as timestampedMessage);
      } else {
        const newValue = {
          timestamp: Date.now(),
          content: 'Could not Start Task',
          level: 'error',
        };
        setSnackBar(newValue as timestampedMessage);
      }
    } catch (error) {
      console.error('Error processing tasks:', error);
    }
    // reloadList();
  };

  const handleAddNote = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/${pickuptaskId}`;
    const requestData = {
      notes: [template],
    };
    return axios.patch(endpoint, requestData, config);
  };

  const handleMarkAsFailedMenuClick = () => {
    setMarkAsFailedDialogOpen(true);
    handleMenuClose();
  };

  const handleMarkAsFailedTasks = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/${pickuptaskId}`;
    const requestData = {
      status: PickupTaskState.FAILED,
      failure_reason: template,
    };

    return axios.patch(endpoint, requestData, config);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  async function scanOrderOperation(
    o: string,
    operation: 'scan' | 'unscan'
  ): Promise<boolean> {
    try {
      const requestData = {
        delivery_state:
          operation == 'scan'
            ? DeliveryState.SCANNED_FOR_PICKUP
            : DeliveryState.PICKUP_ASSIGNED,
      };
      const url = `${API_ENDPOINT}/api/v1/order/${o}/`;
      console.log(url);
      const newResponse = await axios.patch(url, requestData, config);
      setResponse(newResponse);
      if (response?.status === 200) return true;
      else return false;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        return false;
      } else {
        return false;
      }
    }
  }

  React.useEffect(() => {
    const getAndSetTaskDetails = async () => {
      if (pickuptaskId !== undefined) {
        setIsLoading(true);
        try {
          const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/${pickuptaskId}`;
          const response = await axios.get<IPickupTask>(endpoint, config);
          setResponse(response);
          setPickupData(response.data);
          console.log(response.data);
        } catch (e) {
          setPickupData(undefined);
          if (axios.isAxiosError(e)) {
            setMessage('Failed to load pickup data: ' + e.message);
          } else {
            setMessage('Failed to load pickup data');
          }
          console.log(e);
        }
        setIsLoading(false);
      }
    };
    const fetchTemplates = async () => {
      try {
        const endpoint = `${API_ENDPOINT}/api/v1/settings`;
        const response = await axios.get<OrganisationSettings>(
          endpoint,
          config
        );
        setNotificationTemplates(response.data.notification_templates);
        console.log(response.data);
      } catch (e) {
        setSnackBar({
          timestamp: Date.now(),
          content: 'Failed to load notification templates',
          level: 'error',
        });
      }
    };
    getAndSetTaskDetails();
    fetchTemplates();
  }, [pickuptaskId]);

  const completePickupTask = async (
    orders: DecodingWithOrderVal[],
    setIsLoading: (isLoading: boolean) => void,
    setResponse: (resp?: AxiosResponse) => void,
    setMessage: (m: string) => void,
    config: any
  ) => {
    setIsLoading(true);
    setResponse(undefined);
    try {
      const requestData = {
        status: PickupTaskState.PICKED_UP,
        pending_barcodes: orders
          .filter((item) => item.type === 'temporary')
          .map((item) => item.id),
      };

      const url = `${API_ENDPOINT}/api/v1/pickup-task/${pickupData?.id}/`;
      const newResponse = await axios.patch(url, requestData, config);
      setResponse(newResponse);
      setMessage('Pickup task completed successfully.');
      changeTaskStatus(PickupTaskState.PICKED_UP);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(`Failed to complete task: ${e.response?.statusText}`);
      } else {
        setMessage('Failed to complete task.');
      }
    }

    setIsLoading(false);
  };

  let expectedPackages: DecodingWithOrderVal[] = [];
  let scannedPackages: DecodingWithOrderVal[] = [];

  if (pickupData) {
    console.log('Task = ', pickupData.pending_barcodes);
    expectedPackages = transformOrdersToDecodingWithOrderVal(pickupData.orders);
    scannedPackages = [
      ...transformOrdersToDecodingWithOrderVal(
        pickupData.orders.filter(
          (order) => order.delivery_state !== DeliveryState.PICKUP_ASSIGNED
        )
      ),
      ...transformPendingBarcodesToDecodingWithOrderVal(
        pickupData.pending_barcodes ?? []
      ),
    ];
  }

  console.log(scannedPackages);

  useEffect(() => {
    if (pickupData) {
      console.log(pickupData);
      setPickupData({
        ...pickupData,
        package_count:
          pickupData.orders
            .map((item) => item.number_of_packages)
            .reduce((a, b) => a + b, 0) +
          (pickupData.pending_barcodes?.length ?? 0),
      });
    }
  }, [pickupData?.orders, pickupData?.pending_barcodes]);

  return (
    <>
      <Box sx={{ p: 2 }}>
        {isLoading ? (
          <LinearProgress />
        ) : (
          <>
            {pickupData ? (
              <>
                <Typography variant="h3" textAlign="center">
                  Pickup #{pickupData.id}
                </Typography>
                <QRCodeBox
                  value={pickupTaskToQRcodeID(pickupData)}
                  centered
                  size={100}
                />
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <ItemsCount label={`# items: ${pickupData?.package_count}`} />
                </div>
                <div style={{ textAlign: 'center', width: '100%' }}>
                  {showPickupTaskState(pickupData.status)}
                </div>
                {pickupData.status != PickupTaskState.COMPLETED &&
                  pickupData.status != PickupTaskState.FAILED &&
                  pickupData.status != PickupTaskState.CANCELLED && (
                    <>
                      <hr />
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          mt: 0,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {pickupData.status == PickupTaskState.ASSIGNED && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleStartOrResumeTaskMenuClick()}
                          >
                            Start
                          </Button>
                        )}
                        {/* {pickupData.status == PickupTaskState. &&
                  <Button size="small" variant="contained" onClick={() => handleStartOrResumeTaskMenuClick()}>Resume</Button>} */}
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setWhatsAppDialogOpen(true)}
                        >
                          Notify
                        </Button>

                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddNoteMenuClick()}
                        >
                          Add Note
                        </Button>
                        {pickupData.status != PickupTaskState.PICKED_UP && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleMarkAsFailedMenuClick()}
                          >
                            Fail
                          </Button>
                        )}
                      </Box>
                    </>
                  )}
                <hr />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <IconWithText centered>
                    <MonetizationOn
                      color="primary"
                      fontSize="large"
                    ></MonetizationOn>{' '}
                    <div style={{ fontWeight: 'bold' }}>
                      Pay on Pickup:{' '}
                      {displayMoney(
                        add(
                          compute_total_pop(pickupData.orders),
                          pickupData.pop
                        )
                      )}
                    </div>
                  </IconWithText>

                  <Typography variant="body2" color="text.secondary">
                    Creation Date:{' '}
                    {format(
                      new Date(pickupData.creation_date ?? ''),
                      'dd-MM-yyyy'
                    )}
                  </Typography>

                  {pickupData.workflow == Workflow.STANDARD && (
                    <>
                      <IconWithText>
                        <StorefrontIcon color="primary" />
                        <b>
                          {' '}
                          {pickupData.merchant?.name}{' '}
                          {pickupData.merchant_location?.contact_phone}
                        </b>
                      </IconWithText>
                      <AddressWithIcon address={pickupData.merchant_location} />
                    </>
                  )}

                  {pickupData.workflow == Workflow.RETURN && (
                    <>
                      <IconWithText>
                        <PersonOutlineIcon color="primary" />
                        <b>
                          {' '}
                          {pickupData.customer?.name}{' '}
                          {pickupData.customer?.phone_number}{' '}
                          {pickupData.customer?.secondary_phone_number &&
                            `(${display_phone_number(pickupData.customer.secondary_phone_number)})`}
                        </b>
                      </IconWithText>
                      <AddressWithIcon address={pickupData.customer_location} />
                    </>
                  )}
                  {(pickupData.notes ?? []).length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Notes:</Typography>
                      <Typography variant="body2" style={{ color: red[400] }}>
                        {(pickupData.notes ?? []).join(' | ')}
                      </Typography>
                    </Box>
                  )}
                  <StaticGoogleMap
                    mapPath={extractMapsPlaceLink(
                      pickupData.workflow == Workflow.STANDARD
                        ? pickupData.merchant_location
                        : pickupData.customer_location
                    )}
                    url={
                      pickupData.workflow == Workflow.STANDARD
                        ? pickupData.merchant_location?.url
                        : pickupData.customer_location?.url
                    }
                  />
                </Box>
                <br />
                {(pickupData.status === PickupTaskState.IN_PROGRESS ||
                  pickupData.status === PickupTaskState.PICKED_UP) && (
                  <MultiPackageScanner
                    confirmationContent={
                      <>
                        <div>
                          <p style={{ fontWeight: 'bold' }}>
                            # Collected Packages: {pickupData.package_count}
                          </p>
                          <p style={{ fontWeight: 'bold' }}>
                            Paid:{' '}
                            {displayMoney(
                              add(
                                compute_total_pop(pickupData.orders),
                                pickupData.pop
                              )
                            )}
                          </p>
                        </div>
                      </>
                    }
                    label={'package id'}
                    disableCallbackOnNothingScanned
                    disabled={pickupData.status !== PickupTaskState.IN_PROGRESS}
                    acceptTemporaryOrders={true}
                    commitFun={async (
                      decoding: DecodingWithOrderVal,
                      scannedPkgs: DecodingWithOrderVal[]
                    ) => {
                      if (decoding.type === 'temporary') {
                        try {
                          const pendingBarcodes = Array.from(
                            new Set(
                              scannedPkgs.filter(
                                (dec) => dec.type === 'temporary'
                              )
                            )
                          ).map((item) => item.id);
                          console.log(pendingBarcodes);
                          const response = await axios.patch(
                            `${API_ENDPOINT}/api/v1/pickup-task/${pickuptaskId}/`,
                            {
                              pending_barcodes: [
                                `${decoding.id}`,
                                ...pendingBarcodes,
                              ],
                            },
                            config
                          );
                          setPickupData({
                            ...pickupData,
                            pending_barcodes: [
                              `${decoding.id}`,
                              ...pendingBarcodes,
                            ],
                          });
                          return true;
                        } catch (e) {
                          if (axios.isAxiosError(e)) console.log(e.response);
                          return false;
                        }
                      }
                      if (
                        decoding.number_of_packages ==
                        scannedPkgs.filter((pkg) => pkg.id === decoding.id)
                          .length +
                          1
                      )
                        return await scanOrderOperation(
                          decoding.order.id,
                          'scan'
                        );

                      return true;
                    }}
                    rollbackFun={async (
                      decoding: DecodingWithOrderVal,
                      scannedPkgs: DecodingWithOrderVal[]
                    ) => {
                      if (decoding.type === 'temporary') {
                        try {
                          const pendingBarcodes = Array.from(
                            new Set(
                              scannedPkgs.filter(
                                (dec) =>
                                  dec.type === 'temporary' &&
                                  dec.id != decoding.id
                              )
                            )
                          ).map((item) => item.id);
                          console.log(pendingBarcodes);
                          await axios.patch(
                            `${API_ENDPOINT}/api/v1/pickup-task/${pickuptaskId}/`,
                            {
                              pending_barcodes: pendingBarcodes,
                            },
                            config
                          );
                          setPickupData({
                            ...pickupData,
                            pending_barcodes: pendingBarcodes,
                          });
                          return true;
                        } catch (e) {
                          if (axios.isAxiosError(e)) console.log(e.response);
                          return false;
                        }
                      }
                      if (
                        decoding.number_of_packages ==
                        scannedPkgs.filter((pkg) => pkg.id === decoding.id)
                          .length
                      )
                        return await scanOrderOperation(
                          decoding.order.id,
                          'unscan'
                        );

                      return true;
                    }}
                    validator={(decodingWithVal: DecodingWithOrderVal) => {
                      if (
                        decodingWithVal.order.delivery_state !==
                        DeliveryState.PICKUP_ASSIGNED
                      )
                        return {
                          validated: false,
                          error: 'order not assigned for pickup.',
                        };

                      return { validated: true };
                    }}
                    callback={completePickupTask}
                    actionType={'Complete Pickup'}
                    expectedOrders={expectedPackages}
                    defaultScannedOrders={scannedPackages}
                    getRenderOrdersProps={function (
                      decodings: DecodingWithOrderVal[]
                    ): GenericTableProps<DecodingWithOrderVal> {
                      return {
                        sourceView: "driver-pickup-task",
                        items: decodings,
                        fullWidth: true,
                        columns: [
                          {
                            id: 'order_id',
                            header: 'Order ID',
                            display_body: (decoding) => (
                              <>
                                {decoding.order.order_id}{' '}
                                {decoding.type === 'temporary' && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignContent: 'center',
                                      justifyItems: 'center',
                                      gap: 2,
                                    }}
                                  >
                                    <HourglassBottomIcon fontSize="small" />
                                    {decoding.id}
                                  </div>
                                )}
                              </>
                            ),
                          },
                        ],
                        getItemUID: (decoding) => generateUID(decoding),
                        selectedItems: new Map(),
                        isLoading: false,
                      };
                    }}
                    allowUndoScan={
                      pickupData.status === PickupTaskState.IN_PROGRESS
                    }
                    resetAllScan={
                      pickupData.status === PickupTaskState.IN_PROGRESS
                        ? handleResetAllScannedOrders
                        : undefined
                    }
                  />
                )}
                {markAsFailedDialogOpen && (
                  <MarkAsFailedDialog
                    setSnackBarResult={(t?: timestampedMessage) =>
                      setSnackBar(t)
                    }
                    onSubmit={handleMarkAsFailedTasks}
                    onClose={() => setMarkAsFailedDialogOpen(false)}
                    failure_reasons={pickup_failure_reasons}
                    onSuccess={(r) =>
                      setPickupData({
                        ...pickupData,
                        status: PickupTaskState.FAILED,
                      })
                    }
                  />
                )}
                {addNoteDialogOpen && (
                  <AddNotesDialog
                    setSnackBarResult={(t?: timestampedMessage) =>
                      setSnackBar(t)
                    }
                    onSubmit={handleAddNote}
                    onClose={() => setAddNoteDialogOpen(false)}
                    onSuccess={(t) => {
                      const notes: string[] = pickupData.notes ?? [];
                      setPickupData({ ...pickupData, notes: [...notes, t] });
                    }}
                  />
                )}
                {whatsAppDialogOpen && notificationTemplates && (
                  <SendMessageDialog
                    stage={'Pickup'}
                    setSnackBarResult={(t?: timestampedMessage) =>
                      setSnackBar(t)
                    }
                    onSubmit={handleSendWhatAppMessage}
                    onClose={() => setWhatsAppDialogOpen(false)}
                    onSuccess={() => {
                      /** to be implemented */
                    }}
                    locallyResolvedTargets={[
                      {
                        id: pickupData.id,
                        label: `Pickup #${pickupData.id}`,
                        phone_number:
                          pickupData.workflow == Workflow.RETURN
                            ? pickupData.customer?.phone_number ?? ''
                            : pickupData.merchant_location?.contact_phone ?? '',
                        alreadyStarted:
                          pickupData.status != PickupTaskState.ASSIGNED,
                        resolveTemplateArgs: buildResolveTemplateArgsForPickup(
                          pickupData,
                          user?.name ?? '',
                          user?.organisation?.org_id ?? 'ERROR'
                        ),
                      },
                    ]}
                    startTask={handleStartTask}
                  />
                )}
              </>
            ) : (
              <LinearProgress />
            )}
          </>
        )}
        {snackBar && snackBar.content && snackBar.content !== '' && (
          <SnackBar
            message={snackBar.content}
            level={snackBar.level}
            key={snackBar.timestamp}
          />
        )}
      </Box>
    </>
  );
}

export default DriverPickupTaskPageOnlyOrder;
